import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBars, faTimes } from '@fortawesome/free-solid-svg-icons';
import LeftNavBar from './LeftNavBar';
import MercenaryBar from './MercenaryBar';
import { useMercenary } from '../../contexts/MercenaryContext';
import { useLocation } from 'react-router-dom';
import styles from './MobileHeader.module.css';

interface MobileHeaderProps {
  user: any;
}

const MobileHeader: React.FC<MobileHeaderProps> = ({ user }) => {
  const [isNavOpen, setIsNavOpen] = useState(false);
  const [isMercenaryOpen, setIsMercenaryOpen] = useState(false);
  const [regenTimerProgress, setRegenTimerProgress] = useState<number>(0);
  const { mercenary } = useMercenary();
  const location = useLocation();

  const toggleNav = () => {
    setIsNavOpen(!isNavOpen);
    setIsMercenaryOpen(false); // Close mercenary menu when opening nav
  };

  const toggleMercenary = () => {
    setIsMercenaryOpen(!isMercenaryOpen);
    setIsNavOpen(false); // Close nav menu when opening mercenary
  };

  const closeMenus = () => {
    setIsNavOpen(false);
    setIsMercenaryOpen(false);
  };

  // Close menus when navigation changes (user clicks a link)
  useEffect(() => {
    closeMenus();
  }, [location.pathname]);

  // Regen timer effect (same as MercenaryBar)
  useEffect(() => {
    const interval = setInterval(() => {
      setRegenTimerProgress(Date.now() % 180000);
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  if (!user) return null;

  return (
    <>
      <div className={styles.mobileHeader}>
        {/* Hamburger Menu Button */}
        <button 
          className={styles.hamburgerButton}
          onClick={toggleNav}
          aria-label="Toggle navigation menu"
        >
          <FontAwesomeIcon icon={isNavOpen ? faTimes : faBars} />
        </button>

        {/* Game Title/Logo */}
        <div className={styles.gameTitle}>
          <span className={styles.titleText}>NJORDHEIM</span>
        </div>

        {/* Mercenary Stats Summary */}
        {mercenary && (
          <div className={styles.mercenaryStats}>
            <div className={styles.statItem}>
              <span className={styles.statLabel}>HP</span>
              <span className={styles.statValue}>{Math.floor(mercenary.health || 0)}/{Math.floor(mercenary.maxHealth || 1)}</span>
            </div>
            <div className={styles.statItem}>
              <span className={styles.statLabel}>XP</span>
              <span className={styles.statValue}>
                {((mercenary.experience || 0) - (mercenary.previousExperience || 0))}/
                {((mercenary.nextExperience || 0) - (mercenary.previousExperience || 0))}
              </span>
            </div>
            <div className={styles.statItem}>
              <span className={styles.statLabel}>Rounds</span>
              <span className={styles.statValue}>
                {Math.floor(mercenary.rounds || 0)}/{mercenary.maxRounds || 0}
              </span>
            </div>
            <div className={styles.statItem}>
              <span className={styles.statLabel}>Regen</span>
              <span className={styles.statValue}>
                {Math.floor((180000 - regenTimerProgress) / 60000)}:
                {Math.floor(((180000 - regenTimerProgress) % 60000) / 1000).toString().padStart(2, '0')}
              </span>
            </div>
          </div>
        )}

        {/* Mercenary Portrait */}
        {mercenary && (
          <button 
            className={styles.mercenaryPortrait}
            onClick={toggleMercenary}
            aria-label="Toggle mercenary menu"
          >
            <img
              src={`/img/avatars/${mercenary.race?.toLowerCase() || 'default'}.${mercenary.sex?.toLowerCase() || 'default'}.${mercenary.avatar || 1}.png`}
              alt={mercenary.name || 'Mercenary'}
              className={styles.portraitImage}
            />
          </button>
        )}
      </div>

      {/* Mobile Navigation Overlay */}
      {isNavOpen && (
        <div className={styles.mobileOverlay} onClick={closeMenus}>
          <div className={styles.mobileNavContainer} onClick={(e) => e.stopPropagation()}>
            <LeftNavBar />
          </div>
        </div>
      )}

      {/* Mobile Mercenary Overlay */}
      {isMercenaryOpen && (
        <div className={styles.mobileOverlay} onClick={closeMenus}>
          <div className={styles.mobileMercenaryContainer} onClick={(e) => e.stopPropagation()}>
            <MercenaryBar />
          </div>
        </div>
      )}
    </>
  );
};

export default MobileHeader;
